import React from "react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Badge from "@/components/foundation/Badge";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import PartnerLink, { AutoLinkPartners } from "@/components/marketing/PartnerLink";

/* Long-form privacy policy. Non-final legal — placeholder for launch. */

const SECTIONS = [
  {
    id: "data-we-collect",
    heading: "Data We Collect",
    body: [
      "When you use ZenMe Capital, we collect information necessary to provide funding intelligence and connect you with funding partners.",
    ],
    subsections: [
      { title: "Banking data", body: "When you connect a business bank account, Mastercard Finicity securely provides ZenMe with read-only access to transactional data — deposits, withdrawals, account balances, and account metadata. We never receive or store your online banking credentials." },
      { title: "Business information", body: "Information you provide about your business — legal name, tax identification, ownership, contact details, and industry — is collected as part of your ZenMe profile." },
      { title: "Contact information", body: "Name, email, phone number, and any additional contact details you provide through the app, this website, or when applying for funding." },
      { title: "Usage data", body: "Standard analytics — device type, page views, feature usage — collected to improve the product experience and diagnose issues." },
    ],
  },
  {
    id: "how-we-use-data",
    heading: "How We Use Data",
    body: [
      "The data we collect is used solely to build and improve our funding intelligence products.",
    ],
    subsections: [
      { title: "Funding readiness analysis", body: "Banking and business data is analyzed to produce your ZenScore and the associated funding readiness insights displayed in the ZenMe app." },
      { title: "Underwriting support", body: "With your explicit consent, structured merchant profiles may be shared with funding partners for the purpose of evaluating a funding application you have initiated." },
      { title: "Product improvement", body: "Aggregated, de-identified data may be used to improve our models, refine ZenScore, and enhance the overall product experience." },
      { title: "Communication", body: "We use contact information to respond to inquiries, deliver transactional messages, and — with your consent — send occasional product updates." },
    ],
  },
  {
    id: "data-sharing",
    heading: "Data Sharing",
    body: [
      "ZenMe Capital does not sell your personal or business data.",
      "We share information only in the following circumstances:",
    ],
    subsections: [
      { title: "Funding partners (with consent)", body: "When you initiate a funding application, relevant merchant profile data is shared with the specific funding partner(s) you have selected or been matched with. This is always initiated by you." },
      { title: "Service providers", body: "Mastercard Finicity provides bank connectivity. Uptiq AI provides the underlying AI/ML platform infrastructure. Cloud hosting, analytics, and communication providers may process data on our behalf under strict contractual controls." },
      { title: "Legal requirements", body: "We may disclose information when required by law, subpoena, or to protect the rights, property, or safety of ZenMe, our users, or the public." },
    ],
  },
  {
    id: "storage-security",
    heading: "Data Storage & Security",
    body: [
      "Your data is protected using industry-standard security practices.",
    ],
    subsections: [
      { title: "Encryption", body: "Data is encrypted at rest using AES-256 and in transit using TLS 1.2 or higher." },
      { title: "Encrypted API connections", body: "All service-to-service traffic — including with Mastercard Finicity, Uptiq AI, and our funding partner integrations — is authenticated and encrypted end-to-end." },
      { title: "Credentials never stored", body: "ZenMe never receives, requests, or stores your online banking credentials. Authentication happens directly between you and your bank via Finicity." },
      { title: "Access controls", body: "Access to production systems and merchant data is restricted, logged, and reviewed regularly." },
    ],
  },
  {
    id: "your-rights",
    heading: "Your Rights",
    body: [
      "You have meaningful control over your data at all times.",
    ],
    subsections: [
      { title: "Access", body: "You can request a copy of the personal and business information ZenMe holds about you." },
      { title: "Deletion", body: "You can request deletion of your data, subject to legal retention requirements (for example, records related to completed funding transactions)." },
      { title: "Disconnect bank", body: "You may revoke your bank connection at any time via the ZenMe app or the Finicity data-sharing dashboard. Once revoked, no new banking data is received." },
      { title: "Opt out", body: "You can opt out of marketing communications at any time via the unsubscribe link in emails or by contacting us." },
    ],
  },
  {
    id: "cookies-analytics",
    heading: "Cookies & Analytics",
    body: [
      "We use a minimal set of cookies and analytics tools to understand how the website is used and to improve the experience. You can control cookie behavior through your browser settings.",
    ],
  },
  {
    id: "third-party",
    heading: "Third-Party Services",
    body: [
      "ZenMe is deliberately built on top of trusted third-party infrastructure. Our primary providers are:",
    ],
    subsections: [
      { title: "Mastercard Finicity", body: "Secure open banking connectivity, used for all merchant bank account connections." },
      { title: "Uptiq AI", body: "AI/ML infrastructure and platform used to build and operate ZenScore." },
    ],
  },
  {
    id: "contact-privacy",
    heading: "Contact",
    body: [
      "For privacy-related questions, requests, or complaints, contact us at privacy@zenmecapital.com.",
    ],
  },
];

export default function Privacy() {
  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · Privacy Policy"
        description="How ZenMe Capital collects, uses, secures, and shares information. Informational draft — the final policy will be published prior to launch."
        path="/privacy"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-12 sm:pt-32 sm:pb-16"
        data-testid="privacy-hero"
      >
        <GlowOrb color="emerald" size={420} intensity={0.14} className="-bottom-40 -left-40" />

        <Container className="relative z-10">
          <div className="max-w-3xl">
            <FadeIn>
              <Badge variant="emerald">Legal</Badge>
            </FadeIn>
            <FadeIn delay={0.06}>
              <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Privacy Policy
              </h1>
            </FadeIn>
            <FadeIn delay={0.14}>
              <p className="mt-4 text-sm text-text-muted">
                Effective date: <span className="font-mono">[placeholder — pre-launch]</span>
              </p>
            </FadeIn>
            <FadeIn delay={0.22}>
              <div className="mt-6 rounded-xl border border-brand-emerald/20 bg-brand-emerald/[0.04] p-4 text-sm text-text-secondary">
                <strong className="text-brand-emerald">Notice:</strong> This
                document is provided for informational purposes. A final,
                legally-reviewed policy will be published prior to launch.
              </div>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* BODY */}
      <Container className="relative z-10 pb-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* TOC */}
          <aside className="hidden lg:col-span-3 lg:block">
            <nav className="sticky top-24 flex flex-col gap-2 border-l border-white/[0.06] pl-4">
              <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted">
                Contents
              </p>
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-xs text-text-secondary transition-colors hover:text-brand-emerald"
                >
                  {s.heading}
                </a>
              ))}
            </nav>
          </aside>

          <div className="lg:col-span-9">
            <div className="flex flex-col gap-14">
              {SECTIONS.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                    {s.heading}
                  </h2>
                  {s.body?.map((p, i) => (
                    <p
                      key={i}
                      className="mt-4 text-sm leading-relaxed text-text-secondary sm:text-base"
                    >
                      <AutoLinkPartners text={p} />
                    </p>
                  ))}
                  {s.subsections && (
                    <div className="mt-6 flex flex-col gap-6">
                      {s.subsections.map((sub) => (
                        <div key={sub.title}>
                          <h3 className="font-display text-lg font-semibold text-white">
                            {sub.title === "Mastercard Finicity" ? (
                              <PartnerLink partner="finicity" showGlyph={false} className="!text-white hover:!text-brand-emerald">
                                {sub.title}
                              </PartnerLink>
                            ) : sub.title === "Uptiq AI" ? (
                              <PartnerLink partner="uptiq" showGlyph={false} className="!text-white hover:!text-brand-emerald">
                                {sub.title}
                              </PartnerLink>
                            ) : (
                              sub.title
                            )}
                          </h3>
                          <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                            <AutoLinkPartners text={sub.body} />
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </PageLayout>
  );
}
